import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, ExternalLink, FileText, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const humanBytes = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * In-app PDF/image preview for admin. Fetches the attachment through the authorized
 * API into a blob URL and renders it in an <iframe> (PDFs) or <img> (raster) so the
 * reviewer stays inside the roster drawer instead of losing context to a new tab.
 *
 * Props:
 *  - open (bool)
 *  - onClose
 *  - userId, itemId, fileId  (identify the file on the server — fileId is required
 *    now that each checklist item can carry multiple attachments)
 *  - filename, contentType, size, uploadedAt (metadata for header/footer chrome)
 */
export default function AttachmentPreviewModal({
  open,
  onClose,
  userId,
  itemId,
  fileId,
  filename,
  contentType,
  size,
  uploadedAt,
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch + build blob URL whenever the modal opens for a new file.
  useEffect(() => {
    if (!open || !userId || !itemId || !fileId) return;
    let cancelled = false;
    let localUrl = null;
    (async () => {
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const resp = await api.get(
          `/admin/photographers/${userId}/checklist/${itemId}/attachment/${fileId}/download`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        const blob = new Blob([resp.data], {
          type: resp.data.type || contentType || "application/octet-stream",
        });
        localUrl = URL.createObjectURL(blob);
        setBlobUrl(localUrl);
      } catch (e) {
        if (!cancelled) setError("Could not load this file.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [open, userId, itemId, fileId, contentType]);

  // ESC to close + body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const isImage = useMemo(() => (contentType || "").startsWith("image/"), [contentType]);
  const isPdf = useMemo(() => (contentType || "") === "application/pdf", [contentType]);

  const downloadFile = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "attachment";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const openNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9990] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="attachment-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ background: "rgba(10,4,8,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[960px] max-h-[92vh] rounded-sm overflow-hidden flex flex-col bg-[#F5EBD9] border border-[#6B0F1A]/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]"
          >
            {/* Header */}
            <div className="flex items-start gap-3 px-5 py-4 border-b border-[#1A0F0A]/10 bg-[#F5EBD9]">
              <FileText className="w-4 h-4 text-[#6B0F1A] mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div className="flex-1 min-w-0">
                <div
                  className="text-sm font-semibold text-[#1A0F0A] truncate"
                  title={filename}
                  data-testid="attachment-preview-filename"
                >
                  {filename || "Attachment"}
                </div>
                <div className="font-mono-tag text-[9px] text-[#78584B] mt-0.5">
                  {(contentType || "").toUpperCase() || "FILE"}
                  {size != null && ` · ${humanBytes(size)}`}
                  {uploadedAt && ` · UPLOADED ${new Date(uploadedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`}
                </div>
              </div>
              <button
                type="button"
                onClick={openNewTab}
                disabled={!blobUrl}
                data-testid="attachment-preview-newtab"
                title="Open in new tab"
                className="p-2 rounded-sm text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 transition-colors disabled:opacity-40"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={downloadFile}
                disabled={!blobUrl}
                data-testid="attachment-preview-download"
                title="Download"
                className="p-2 rounded-sm text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 transition-colors disabled:opacity-40"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="attachment-preview-close"
                title="Close (Esc)"
                className="p-2 rounded-sm text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 min-h-0 bg-[#1A0F0A]/5 flex items-center justify-center overflow-auto">
              {loading && (
                <div className="flex flex-col items-center gap-3 text-[#78584B]">
                  <Loader2 className="w-6 h-6 animate-spin" strokeWidth={2} />
                  <div className="font-mono-tag text-[10px]">LOADING PREVIEW…</div>
                </div>
              )}
              {!loading && error && (
                <div className="flex flex-col items-center gap-2 text-[#6B0F1A] px-6 text-center">
                  <div className="font-mono-tag text-[10px]">{error}</div>
                  <button
                    type="button"
                    onClick={downloadFile}
                    className="mt-2 font-mono-tag text-[10px] underline"
                  >
                    Try downloading instead
                  </button>
                </div>
              )}
              {!loading && !error && blobUrl && (
                isPdf ? (
                  <iframe
                    src={blobUrl}
                    title={filename || "PDF preview"}
                    data-testid="attachment-preview-iframe"
                    className="w-full h-[min(78vh,860px)] bg-white"
                  />
                ) : isImage ? (
                  <img
                    src={blobUrl}
                    alt={filename || "Attachment"}
                    data-testid="attachment-preview-image"
                    className="max-w-full max-h-[78vh] object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-[#78584B] px-6 text-center">
                    <FileText className="w-8 h-8" strokeWidth={1.5} />
                    <div className="font-mono-tag text-[10px]">
                      NO INLINE PREVIEW FOR {(contentType || "THIS FILE").toUpperCase()}
                    </div>
                    <button
                      type="button"
                      onClick={downloadFile}
                      className="mt-1 font-mono-tag text-[10px] underline"
                    >
                      Download to review
                    </button>
                  </div>
                )
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
